// The draft editor's knowledge check (E31-S6-T2; mockups/content-edit.html, "Knowledge check"): the
// question set as the Markdown the owner chose (ADR-023 §1), with line numbers, beside a live check
// that lists the pack parser's own errors by line (@waypoint/content/questions, run in the browser,
// through edit.ts questionProblems). Choosing an error selects its line. While one stands the page
// will not save.
import { cn, focusRing } from "@waypoint/surface";
import { useRef } from "react";
import { t } from "../../i18n/t.js";
import { FieldError, StatusChip } from "../../ui/form.js";
import type { QuestionProblem } from "../../versions/edit.js";
import { readQuestions } from "../../versions/questions.js";

export const QUESTIONS_ID = "qsMd";
const LINE_PX = 20;

/** Focuses the question set with line `line` selected. */
export function focusLine(line: number): void {
  const ta = document.getElementById(QUESTIONS_ID);
  if (!(ta instanceof HTMLTextAreaElement)) return;
  const lines = ta.value.split("\n");
  let at = 0;
  for (let k = 0; k < line - 1 && k < lines.length; k++) at += (lines[k] ?? "").length + 1;
  ta.focus();
  ta.setSelectionRange(at, at + (lines[line - 1] ?? "").length);
  ta.scrollTop = Math.max(0, (line - 4) * LINE_PX);
}

export function QuestionsEditor({
  markdown,
  problems,
  serverRefused,
  onChange,
}: {
  markdown: string;
  problems: readonly QuestionProblem[];
  /** The server's 400 named the question set. */
  serverRefused: boolean;
  onChange: (markdown: string) => void;
}) {
  const gutter = useRef<HTMLPreElement>(null);
  const bad = new Set(problems.map((p) => p.line));
  const lines = markdown.split("\n");
  const shown = problems.length === 0 ? readQuestions(markdown) : undefined;
  const questions = shown?.flatMap((s) => s.questions) ?? [];

  return (
    <div className="grid items-start gap-4 min-[1181px]:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
      <div className="min-w-0">
        <label htmlFor={QUESTIONS_ID} className="mb-1 block text-12 font-semibold text-wp-text-2">
          {t("edit.qs.label")}
        </label>
        <div
          className={cn(
            "grid grid-cols-[auto_minmax(0,1fr)] overflow-hidden rounded-8 border bg-wp-bg/58 focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-wp-accent",
            problems.length > 0 || serverRefused ? "border-stop/48" : "border-wp-line-strong",
          )}
        >
          <pre
            ref={gutter}
            aria-hidden
            className="m-0 overflow-hidden border-r border-wp-line bg-wp-bg/40 py-2 pr-2 pl-2.5 text-right font-mono text-12 leading-5 text-wp-text-3 select-none"
          >
            {lines.map((_, i) => (
              <span
                key={i}
                data-bad={bad.has(i + 1) || undefined}
                className={cn("block", bad.has(i + 1) ? "font-bold text-stop-soft" : "")}
              >
                {i + 1}
              </span>
            ))}
          </pre>
          <textarea
            id={QUESTIONS_ID}
            value={markdown}
            spellCheck={false}
            autoCapitalize="off"
            wrap="off"
            aria-invalid={problems.length > 0 || serverRefused}
            aria-describedby="qsSum qsServer"
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onScroll={(e) => {
              if (gutter.current) gutter.current.scrollTop = e.currentTarget.scrollTop;
            }}
            className="m-0 block min-h-75 w-full resize-y overflow-auto border-0 bg-transparent px-2.5 py-2 font-mono text-12 leading-5 whitespace-pre text-wp-text outline-0 min-[721px]:min-h-95"
          />
        </div>
        <FieldError id="qsServer">{serverRefused ? t("edit.qs.server") : undefined}</FieldError>
        <details className="mt-2.5 text-13 text-wp-text-2">
          <summary className={cn("cursor-pointer font-semibold text-wp-text", focusRing)}>
            {t("edit.qs.grammar")}
          </summary>
          <pre className="mt-2 mb-0 rounded-8 border border-wp-line bg-wp-bg/58 px-2.5 py-2 font-mono text-12 leading-[1.6] whitespace-pre-wrap text-wp-text-2">
            {t("edit.qs.grammarText")}
          </pre>
          <p className="mt-2 mb-0">{t("edit.qs.grammarRule")}</p>
        </details>
      </div>

      <section
        aria-labelledby="qsCheck"
        aria-live="polite"
        className="grid gap-2.5 rounded-8 border border-wp-line bg-wp-panel/74 p-3"
      >
        <h3 id="qsCheck" className="m-0 flex flex-wrap items-center gap-2 text-14 font-semibold">
          {t("edit.qs.check")}
          {problems.length > 0 ? (
            <StatusChip tone="stop">
              {problems.length === 1
                ? t("edit.qs.problem")
                : t("edit.qs.problems", { n: problems.length })}
            </StatusChip>
          ) : (
            <StatusChip tone="done">{t("edit.qs.ok")}</StatusChip>
          )}
        </h3>
        <p id="qsSum" className="m-0 text-13 text-wp-text-2">
          {problems.length === 0
            ? t("edit.qs.okLine")
            : problems.length === 1
              ? t("edit.qs.problemLine")
              : t("edit.qs.problemsLine")}
        </p>
        {problems.length > 0 ? (
          <ul className="m-0 grid list-none gap-1.5 p-0">
            {problems.map((p, i) => (
              <li key={`${String(p.line)}-${String(i)}`}>
                <button
                  type="button"
                  onClick={() => {
                    focusLine(p.line);
                  }}
                  className={cn(
                    "grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5 rounded-8 border border-stop/48 bg-stop/10 px-2.25 py-1.75 text-left font-sans text-13 text-wp-text hover:bg-stop/18",
                    focusRing,
                  )}
                >
                  <span className="pt-px font-mono text-12 font-bold whitespace-nowrap text-stop-soft">
                    {t("edit.qs.line", { line: p.line })}
                  </span>
                  <span>{p.message}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="m-0 grid list-none gap-1 p-0 text-13 text-wp-text-2">
            {questions.map((q, i) => {
              const right = q.options.find((o) => o.right);
              return (
                <li
                  key={i}
                  className="flex flex-wrap items-baseline gap-2 rounded-8 border border-wp-line bg-wp-bg/58 px-2 py-1.25"
                >
                  <span className="font-mono text-12 font-semibold text-wp-text-3">
                    {t("review.qs.q", { n: i + 1 })}
                  </span>
                  <span>
                    {t("edit.qs.okQuestion", {
                      options: q.options.length,
                      right: right?.id ?? "",
                    })}
                  </span>
                  {q.pages === undefined ? null : (
                    <span className="ml-auto rounded-pill border border-wp-accent/44 bg-wp-accent/13 px-1.75 py-0.5 font-mono text-11 font-semibold text-wp-accent">
                      {q.pages}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
